import { useCallback, useEffect, useRef } from "react";
import { createDeviceWebSocketClient, DEVICE_WEBSOCKET_STATE } from "./deviceWebSocketClient";
import { normalizeScopeOfPractice } from "./deviceMessageProtocol";

export const useDeviceWebSocket = ({
  enabled = true,
  endpoint,
  getToken,
  encounterId,
  organizationId,
  scopeOfPractice,
  onMessage,
  onStateChange,
  tokenRefreshEvent,
}) => {
  const clientRef = useRef(null);
  const getTokenRef = useRef(getToken);
  const onMessageRef = useRef(onMessage);
  const onStateChangeRef = useRef(onStateChange);

  getTokenRef.current = getToken;
  onMessageRef.current = onMessage;
  onStateChangeRef.current = onStateChange;

  const normalizedScopes = normalizeScopeOfPractice(scopeOfPractice);
  const scopeKey = JSON.stringify(normalizedScopes);

  useEffect(() => {
    if (!enabled || !endpoint || !encounterId || !organizationId) return undefined;

    const client = createDeviceWebSocketClient({
      endpoint,
      getToken: () => getTokenRef.current?.(),
      encounterId,
      organizationId,
      scopeOfPractice: JSON.parse(scopeKey),
      onMessage: (message) => onMessageRef.current?.(message),
      onStateChange: (state, details) => onStateChangeRef.current?.(state, details),
    });
    clientRef.current = client;
    client.connect();

    const handleOnline = () => client.reconnect();
    const handleTokenRefresh = () => client.reconnect();

    if (typeof window !== "undefined") {
      window.addEventListener("online", handleOnline);
      if (tokenRefreshEvent) {
        window.addEventListener(tokenRefreshEvent, handleTokenRefresh);
      }
    }

    return () => {
      if (typeof window !== "undefined") {
        window.removeEventListener("online", handleOnline);
        if (tokenRefreshEvent) {
          window.removeEventListener(tokenRefreshEvent, handleTokenRefresh);
        }
      }
      client.dispose();
      if (clientRef.current === client) clientRef.current = null;
    };
  }, [enabled, encounterId, endpoint, organizationId, scopeKey, tokenRefreshEvent]);

  const send = useCallback((message) => clientRef.current?.send(message) ?? false, []);
  const reconnect = useCallback(() => clientRef.current?.reconnect() ?? false, []);
  const getState = useCallback(
    () => clientRef.current?.getState() ?? DEVICE_WEBSOCKET_STATE.IDLE,
    [],
  );

  return { send, reconnect, getState };
};
